import { fassungsschild, type SupportKontakt } from '@nac2s/shared'
import { Navigate, NavLink, Outlet } from 'react-router'
import { Hinweis } from '@/teile/Hinweis'
import { Logo } from '@/teile/Logo'
import { Wortmarke } from '@/teile/Wortmarke'
import { cx } from '@/utils/cx'
import { useSitzung } from '../session/SitzungContext.js'

/**
 * Rahmen der Kundenanwendung.
 *
 * **Dieselbe Bauform wie die Kundenverwaltung**, aus demselben Paket: eine
 * weiße Karte auf hellgrauem Grund, die Wortmarke links, die Bereiche als
 * Pillen daneben. Ein Anwender, der beide Oberflächen sieht, soll sie als
 * eine Anwendung erkennen — nur der Beisatz sagt, wo er ist.
 *
 * Die Navigation blendet aus, wofür das Recht fehlt. Das ist reine
 * Sichtbarkeit — durchgesetzt wird serverseitig (SECURITY.md §4). Wer die
 * Adresse von Hand eintippt, bekommt vom Backend 403 und hier die Meldung.
 */
const NAVIGATION = [
  { pfad: '/', beschriftung: 'Übersicht', genau: true, braucht: null },
  { pfad: '/einstellungen', beschriftung: 'Einstellungen', genau: false, braucht: 'settings.own' },
  { pfad: '/verlauf', beschriftung: 'Verlauf', genau: false, braucht: 'history.own' },
  // Immer sichtbar: Ob Kontakte zur Edition gehören, sagt die Seite selbst.
  { pfad: '/kontakte', beschriftung: 'Kontakte', genau: false, braucht: null },
  // Nur mit Recht — und die Seite selbst sagt, ob die Edition sie trägt.
  { pfad: '/auswertung', beschriftung: 'Auswertung', genau: false, braucht: 'auswertung.anrufe' },
] as const

export function AppShell() {
  const { zustand, hatRecht } = useSitzung()

  if (zustand.status === 'unbekannt') {
    return (
      <p role="status" className="p-8 text-sm text-tertiary">
        Wird geladen …
      </p>
    )
  }

  if (zustand.status === 'fehler') {
    return (
      <div className="mx-auto max-w-2xl p-8">
        <Hinweis art="fehler" titel="Der Dienst ist nicht erreichbar">
          {zustand.meldung}
        </Hinweis>
      </div>
    )
  }

  // Ohne Sitzung zur Anmeldung. Bis zum 14.09.2026 stand hier nur das Logo
  // und sonst nichts (Festlegung vom 11.09.). Seit es einen zweiten Weg
  // hinein gibt — E-Mail und Passwort, für Geräte ohne App (ADR-0004
  // Nachtrag 5) —, wäre das eine Sackgasse: Wer kein gekoppeltes Gerät hat,
  // käme nirgends an.
  if (zustand.status === 'abgemeldet') return <Navigate to="/anmelden" replace />

  return (
    <div className="min-h-screen bg-secondary px-4 py-10">
      <div className="mx-auto max-w-6xl overflow-hidden rounded-3xl bg-primary shadow-lg">
        <header className="flex flex-wrap items-center gap-3.5 border-b border-secondary px-5 py-[13px]">
          <Wortmarke beisatz="Kundenanwendung" />

          <nav aria-label="Hauptbereiche" className="ml-1 flex gap-0.5">
            {NAVIGATION.filter(
              (eintrag) => eintrag.braucht === null || hatRecht(eintrag.braucht),
            ).map((eintrag) => (
              <NavLink
                key={eintrag.pfad}
                to={eintrag.pfad}
                end={eintrag.genau}
                className={({ isActive }) =>
                  cx(
                    'rounded-full px-3.5 py-1.5 text-sm transition-colors',
                    isActive
                      ? 'bg-akzent-flaeche font-semibold text-akzent-tief'
                      : 'text-tertiary hover:bg-secondary hover:text-primary',
                  )
                }
              >
                {eintrag.beschriftung}
              </NavLink>
            ))}
          </nav>

          <div className="ml-auto flex items-center gap-3">
            {/* Es gibt hier kein Abmelden: Der Zugang haengt am gekoppelten
                Arbeitsplatz (ADR-0004), nicht an einem Konto. */}
            <span className="hidden items-center gap-2 text-xs text-tertiary sm:flex">
              <span className="size-[7px] animate-pulse rounded-full bg-fg-success-primary" />
              Arbeitsplatz gekoppelt
            </span>
          </div>
        </header>

        <Outlet />

        <Fusszeile kontakt={zustand.sitzung.partner} />
      </div>
    </div>
  )
}

/**
 * Wer diesem Kunden hilft — sein Partner, nicht netable.
 *
 * Ein Satz, drei Angaben: Firma, Telefon, E-Mail. Die Daten pflegt der
 * Partner in der Kundenverwaltung. Solange dort nichts steht, nennt die
 * Fußzeile nur die Fassung — ein leeres Hilfeversprechen ohne Adresse wäre
 * schlimmer als keines.
 */
function Fusszeile({ kontakt }: { kontakt: SupportKontakt | null }) {
  const angaben =
    kontakt === null
      ? []
      : [
          kontakt.name ? <span key="name">{kontakt.name}</span> : null,
          kontakt.telefon ? (
            <a
              key="telefon"
              className="hover:text-primary"
              href={`tel:${kontakt.telefon.replace(/\s+/g, '')}`}
            >
              {kontakt.telefon}
            </a>
          ) : null,
          kontakt.email ? (
            <a key="email" className="hover:text-primary" href={`mailto:${kontakt.email}`}>
              {kontakt.email}
            </a>
          ) : null,
        ].filter((a) => a !== null)
  return (
    <footer className="flex flex-wrap items-baseline gap-x-2 gap-y-1 border-t border-secondary px-6 py-4 text-[12.5px] text-tertiary">
      {angaben.length > 0 ? (
        <>
          <span className="font-medium text-secondary">Sie brauchen Hilfe? Dann hier melden:</span>
          {angaben.map((angabe, i) => (
            <span key={angabe.key} className="flex items-baseline gap-x-2">
              {i > 0 ? (
                <span aria-hidden="true" className="text-quaternary">
                  |
                </span>
              ) : null}
              {angabe}
            </span>
          ))}
        </>
      ) : null}
      <span className="ml-auto">
        <Fassungszeile />
      </span>
    </footer>
  )
}

/**
 * Die ausgelieferte Fassung, klein und immer sichtbar.
 *
 * `DOCUMENTATION.md` §1: Neben der Nummer steht der Git-Kurz-Hash — er
 * unterscheidet zwei Stände derselben Nummer. Der Hash kommt beim Bauen aus
 * `VITE_NADIA_BUILD`; fehlt er, bleibt es bei der Nummer.
 */
function Fassungszeile() {
  return (
    <span className="text-[11.5px] text-quaternary tabular-nums" title="Ausgelieferte Fassung">
      VeeKy {fassungsschild(import.meta.env.VITE_NADIA_BUILD)}
    </span>
  )
}

/**
 * Nur das Logo — für den Fall, dass es nichts zu zeigen gibt.
 *
 * Bis zum 14.09.2026 war das die Antwort auf jeden Aufruf ohne Sitzung: Diese
 * Adresse ist öffentlich erreichbar, und wer ohne Sitzung hier landete, bekam
 * bewusst keine Erklärung (Festlegung des Inhabers, 11.09.2026). Seit es die
 * Anmeldung mit E-Mail und Passwort gibt, führt der Weg dorthin. Die
 * Sparsamkeit der Auskunft bleibt: Die Anmeldung erklärt nicht, wie die
 * Kopplung funktioniert.
 */
export function NichtAngemeldet() {
  return (
    <main className="flex min-h-screen items-center justify-center p-8">
      <Logo className="w-[23.5rem] [@media(min-height:880px)]:w-[30.5rem]" />
    </main>
  )
}

/** Kopf einer Seite — wie in der Kundenverwaltung: Beisatz, Titel, ein Satz. */
export function Seitenkopf({
  beisatz,
  titel,
  satz,
}: {
  beisatz: string
  titel: string
  satz: string
}) {
  return (
    <div className="relative overflow-hidden px-6 pt-6 pb-[18px]">
      <div
        aria-hidden="true"
        className="schimmer pointer-events-none absolute -top-[13rem] -right-[6rem] size-[25rem]"
      />
      <div className="relative">
        <p className="text-[13px] text-quaternary">{beisatz}</p>
        <h1 className="mt-4 font-display text-[26px] leading-[1.1] font-extrabold tracking-[-0.025em] text-primary">
          {titel}
        </h1>
        <p className="mt-1 text-sm text-tertiary">{satz}</p>
      </div>
    </div>
  )
}
